import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom'
import { ChevronLeft, Save, Trash2, AlertCircle, LayoutTemplate, PanelRightClose, PanelRightOpen, Pencil } from 'lucide-react'
import jsyaml from 'js-yaml'
import { useBuilderObjectType } from './hooks/useBuilderObjectTypes'
import { useBuilderObject, useBuilderObjects } from './hooks/useBuilderObjects'
import { useBuilderModelsFor } from './hooks/useBuilderPages'
import { BuilderTopHeader } from './BuilderNavTabs'
import { BuilderProvider } from './BuilderContext'
import { BuilderCanvas } from './BuilderCanvas'
import { getEditor } from './editors/registry'
import { FieldEditorPopover } from './editors/FieldEditorPopover'
import { applyContextMapping, resolveContextField } from './editable/contextFields'
import { EMPTY_FIELD_VALUE, hasFieldValue } from './editors/fieldValue'
// Amorce le registre des éditeurs (text, image-drupal, lien, liste…) : l'écran
// peut être atteint sans être passé par le shell du builder.
import './editors'

/**
 * Page d'édition d'une instance d'objet (`builder_unit`).
 *
 * Routes :
 *   /builder/types/:objectType/new            — création (id absent)
 *   /builder/types/:objectType/instance/:id   — édition d'une instance
 *
 * DEUX SURFACES pour la même donnée, et un seul enregistrement :
 *
 *  - le FORMULAIRE — les champs du `fields[]` du def, rendus par le registre
 *    d'éditeurs (`getEditor(field.type)`). Inline pour les types qui s'éditent
 *    en place (text, textarea, icône), pop-in ANCRÉE au champ pour les autres.
 *  - un MODÈLE — une page qui déclare servir de gabarit à ce type
 *    (`modelFor`), montée en LUNETTE au-dessus de l'objet : le contenu
 *    s'édite là où il s'affichera. Le modèle n'est pas modifiable ici
 *    (`editScope: 'object'`, cf. BuilderContext) et seuls ses éléments liés à
 *    un champ réel du type le sont — c'est `resolveContextField` qui tranche.
 *    Ce qui part au save reste l'OBJET : ni la page, ni ses blocs, ni les
 *    unités partagées qu'elle monte.
 *
 * Le mode vit dans l'URL, donc il se partage et se met en favori :
 *   ?model=<slug de la page>  → cette lunette
 *   ?model=                   → le formulaire, explicitement
 *   (paramètre absent)        → `defaultModel:` du def, sinon le formulaire
 */
export const BuilderObjectEditor = ({ embedded = false }) => {
  // Sous le menu orbit (`embedded`, routes `withMenu`) : remplir le `main`
  // d'OrbitChrome plutôt qu'imposer 100vh, sinon la page dépasse d'une hauteur
  // de barre de menu. Standalone : plein écran comme avant.
  const shellClass = `${embedded ? 'flex-1' : 'min-h-screen'} bg-gray-50 flex flex-col`

  const { objectType: machineName, id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const isNew = !id

  const { type, loading: typeLoading, error: typeError } = useBuilderObjectType(machineName)
  const { object, loading: objectLoading, error: objectError, saveObject } = useBuilderObject(machineName, id)
  const { deleteObject } = useBuilderObjects(machineName)
  const { models, loading: modelsLoading } = useBuilderModelsFor(machineName)

  const parsedDef = useMemo(() => {
    if (!type?.definition) return null
    try {
      return jsyaml.load(type.definition) || {}
    } catch {
      return null
    }
  }, [type?.definition])

  const fields = useMemo(() => parsedDef?.fields || [], [parsedDef])
  const primaryField = parsedDef?.primaryField || null

  const [data, setData] = useState({})
  const [slug, setSlug] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [modal, setModal] = useState(null) // {key} of the field whose editor is open
  const [panelOpen, setPanelOpen] = useState(true)

  // Initial population : defaultData for new instances, loaded data otherwise.
  useEffect(() => {
    if (isNew && parsedDef) {
      setData(parsedDef.defaultData || {})
      setSlug('')
    } else if (object) {
      setData(object.data || {})
      setSlug(object.slug || '')
    }
  }, [isNew, parsedDef, object])

  const setField = (key, value) => {
    setData(prev => ({ ...prev, [key]: value }))
  }

  // ─── Le modèle courant ────────────────────────────────────────────────────
  // `null` = paramètre absent (aucune préférence exprimée) ; `''` = formulaire
  // demandé explicitement. La distinction compte : sans elle, revenir au
  // formulaire depuis un modèle par défaut serait impossible — l'URL nettoyée
  // rouvrirait aussitôt le modèle.
  const modelParam = searchParams.get('model')
  const requestedSlug = modelParam !== null ? modelParam : (parsedDef?.defaultModel ?? '')
  const activeModel = requestedSlug ? models.find(m => m.slug === requestedSlug) ?? null : null
  const modelPending = requestedSlug !== '' && modelsLoading
  const modelMissing = requestedSlug !== '' && !modelsLoading && !activeModel

  const selectModel = (nextSlug) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('model', nextSlug)
      return next
    }, { replace: true })
  }

  // Ce que le canevas édite : l'objet, jamais la page. `resolve` est la règle
  // d'éditabilité (une liaison `~~._context_.X` qui tombe sur un champ réel du
  // type), `getValue`/`setValue` la lecture/écriture de CE champ-là.
  //
  // La valeur est lue dans une référence toujours à jour plutôt que dans l'état
  // capturé : deux commits successifs (une pop-in qui écrit l'adresse ET
  // l'intitulé d'un lien) liraient sinon tous les deux la donnée d'avant.
  const dataRef = useRef(data)
  dataRef.current = data
  const objectEdit = useMemo(() => ({
    fields,
    mapping: activeModel?.mapping ?? null,
    resolve: (path) => resolveContextField(path, { fields, mapping: activeModel?.mapping ?? null }),
    getValue: (key) => dataRef.current?.[key],
    setValue: (key, value) => setData(prev => ({ ...prev, [key]: withLocalRender(fields, key, value) })),
  }), [fields, activeModel])

  // Le contexte injecté dans les blocs du modèle — exactement celui que le
  // rendu public fabrique pour un visiteur (même helper), pour que l'éditeur
  // voie ce que le visiteur verra. La donnée tenue ici est celle du display
  // decoupled, donc déjà ENRICHIE (`{mid, alt, url}` pour une image) : le
  // serveur restrippe les clés dérivées au presave, l'aller-retour est
  // symétrique et l'aperçu est juste dès le commit.
  const objectContext = useMemo(
    () => applyContextMapping(data, activeModel?.mapping),
    [data, activeModel],
  )

  const handleSave = async () => {
    setSaving(true)
    setSaveError(null)
    try {
      const result = await saveObject({
        nid: object?.nid,
        slug: slug || undefined,
        data,
        // Un contenu NEUF naît sur le site de son type (le back le prendrait
        // par défaut ; on le dit). Sans effet sur une mise à jour.
        siteId: isNew ? (type?.siteId ?? null) : null,
      })
      if (isNew) {
        // After create, redirect to the canonical URL so the form sticks
        // around for further edits with proper nid + slug — en gardant la
        // lunette ouverte (`location.search`), sinon créer dans un modèle
        // renverrait au formulaire à la première sauvegarde.
        navigate(
          `/builder/types/${machineName}/instance/${result.slug || result.nid}${location.search}`,
          { replace: true },
        )
      }
    } catch (e) {
      setSaveError(e.message || 'Erreur de sauvegarde.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!object?.nid) return
    if (!window.confirm(`Supprimer cette instance ?`)) return
    try {
      await deleteObject(object.nid)
      navigate(`/builder/types/${machineName}`)
    } catch (e) {
      alert(e.message || 'Suppression impossible.')
    }
  }

  if (typeLoading || (objectLoading && !isNew)) {
    return (
      <div className={shellClass}>
        <BuilderTopHeader />
        <div className="flex items-center justify-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-teal-700" />
        </div>
      </div>
    )
  }

  if (typeError || !type || !parsedDef) {
    return (
      <div className={shellClass}>
        <BuilderTopHeader />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <AlertCircle size={28} className="text-amber-500 mx-auto mb-3" />
          <p className="text-sm text-gray-600">
            Type « {machineName} » introuvable ou définition YAML invalide.
          </p>
          <Link
            to="/builder/types"
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-teal-700 hover:text-teal-900 transition-colors"
          >
            <ChevronLeft size={14} />
            Retour aux types
          </Link>
        </div>
      </div>
    )
  }

  if (!isNew && (objectError || !object)) {
    return (
      <div className={shellClass}>
        <BuilderTopHeader />
        <div className="max-w-3xl mx-auto px-6 py-16 text-center">
          <AlertCircle size={28} className="text-amber-500 mx-auto mb-3" />
          <p className="text-sm text-gray-600">Instance « {id} » introuvable.</p>
          <Link
            to={`/builder/types/${machineName}`}
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-teal-700 hover:text-teal-900 transition-colors"
          >
            <ChevronLeft size={14} />
            Retour aux instances
          </Link>
        </div>
      </div>
    )
  }

  const titleForDisplay = primaryField && data[primaryField]
    ? data[primaryField]
    : (object?.label || (isNew ? 'Nouvelle instance' : `#${object?.nid}`))

  const header = (
    <BuilderTopHeader
      rightSlot={
        <div className="flex items-center gap-2">
          <ModelPicker
            models={models}
            loading={modelsLoading}
            value={activeModel?.slug ?? ''}
            onChange={selectModel}
          />
          {activeModel && (
            <button
              type="button"
              onClick={() => setPanelOpen(v => !v)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition-colors"
              aria-expanded={panelOpen}
              aria-controls="object-fields-panel"
            >
              {panelOpen ? <PanelRightClose size={14} aria-hidden="true" /> : <PanelRightOpen size={14} aria-hidden="true" />}
              Champs
            </button>
          )}
          {!isNew && (
            <button
              onClick={handleDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <Trash2 size={14} aria-hidden="true" />
              Supprimer
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Save size={14} aria-hidden="true" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      }
    />
  )

  const breadcrumb = (
    <div>
      <Link
        to={`/builder/types/${machineName}`}
        className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-800 transition-colors mb-2"
      >
        <ChevronLeft size={12} aria-hidden="true" />
        {type.name}
      </Link>
      <h1 className="text-2xl font-semibold text-gray-800">{titleForDisplay}</h1>
      {!isNew && (
        <p className="text-xs font-mono text-gray-400 mt-0.5">
          {type.type} / {slug || `#${object.nid}`}
        </p>
      )}
    </div>
  )

  const slugSection = (
    <section className="bg-white border border-gray-200 rounded-xl p-6">
      <label className="block text-xs font-medium text-gray-500 mb-1.5" htmlFor="object-slug">
        Slug <span className="text-gray-300">(optionnel — dérivé du champ primaire si vide)</span>
      </label>
      <input
        id="object-slug"
        type="text"
        value={slug}
        onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
        placeholder={isNew ? 'auto-généré à la sauvegarde' : ''}
        className="w-full px-3 py-2 text-sm font-mono border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent"
      />
    </section>
  )

  const errorBanner = saveError && (
    <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 text-sm text-red-700">
      {saveError}
    </div>
  )

  const popover = modal && (() => {
    const field = fields.find(f => f.key === modal.key)
    if (!field) return null
    return (
      <FieldEditorPopover
        anchorEl={modal.anchorEl}
        field={field}
        value={data[field.key]}
        onSave={(key, v) => {
          setField(key, v)
          setModal(null)
        }}
        onClose={() => setModal(null)}
      />
    )
  })()

  // ─── Édition dans un modèle ───────────────────────────────────────────────
  if (activeModel || modelPending) {
    return (
      <div className={shellClass}>
        {header}
        <div className="px-6 py-2.5 bg-white border-b border-gray-100 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-sm font-medium text-gray-700 truncate">{titleForDisplay}</span>
          <p className="text-xs text-gray-500">
            Contenu affiché dans le modèle «&nbsp;{activeModel?.title ?? '…'}&nbsp;».
            Le modèle n'est pas modifiable ici : seuls les éléments liés à un champ de ce contenu s'éditent.
          </p>
          {activeModel && (
            <Link
              to={`/builder/${activeModel.slug}`}
              className="inline-flex items-center gap-1 text-xs text-teal-700 hover:text-teal-900 transition-colors"
            >
              <Pencil size={12} aria-hidden="true" />
              Modifier le modèle
            </Link>
          )}
        </div>

        <div className="flex-1 flex items-stretch min-h-0">
          <div className="flex-1 min-w-0 flex flex-col">
            {modelPending ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-teal-700" />
              </div>
            ) : (
              // `key` sur le slug : changer de modèle remonte le provider, donc
              // la page chargée, plutôt que de rejouer un chargement dans un
              // état qui décrit encore l'autre.
              <BuilderProvider
                key={activeModel.slug}
                slug={activeModel.slug}
                initialEditMode
                editScope="object"
                objectEdit={objectEdit}
              >
                <BuilderCanvas slug={activeModel.slug} objectContext={objectContext} />
              </BuilderProvider>
            )}
          </div>

          {panelOpen && (
            <aside
              id="object-fields-panel"
              aria-label="Champs du contenu"
              // Colonne de flux, pas de panneau à défilement propre : le
              // builder laisse défiler le DOCUMENT (cf. BuilderShell) — un
              // ascenseur imbriqué ici en ajouterait un second.
              className="w-[340px] shrink-0 border-l border-gray-200 bg-gray-50"
            >
              <div className="p-5 space-y-5">
                <div>
                  <h2 className="text-sm font-semibold text-gray-800">Champs du contenu</h2>
                  <p className="text-xs text-gray-500 mt-1">
                    Tous les champs de ce contenu, qu'ils soient affichés ou non par ce
                    modèle : c'est ici qu'on atteint ceux qu'il ne montre pas — et qu'on
                    vide ceux dont on ne veut plus.
                  </p>
                </div>
                <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-5">
                  {fields.length === 0 && (
                    <p className="text-sm text-gray-400 text-center py-4">
                      Aucun champ déclaré dans le schéma.
                    </p>
                  )}
                  {fields.map((field) => (
                    <FieldRow
                      key={field.key}
                      field={field}
                      value={data[field.key]}
                      onChange={(v) => setField(field.key, v)}
                      onOpenModal={(e) => setModal({ key: field.key, anchorEl: e?.currentTarget ?? null })}
                    />
                  ))}
                </section>
                {slugSection}
                {errorBanner}
              </div>
            </aside>
          )}
        </div>

        {popover}
      </div>
    )
  }

  // ─── Formulaire ───────────────────────────────────────────────────────────
  return (
    <div className={shellClass}>
      {header}

      <main className="max-w-3xl w-full mx-auto px-6 py-10 space-y-6">
        {breadcrumb}

        {modelMissing && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 text-sm text-amber-800 flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              Aucun modèle « {requestedSlug} » ne déclare ce type de contenu — le formulaire est affiché à la place.
            </span>
          </div>
        )}

        {/* Champs du schéma */}
        <section className="bg-white border border-gray-200 rounded-xl p-6 space-y-5">
          {fields.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-6">
              Aucun champ déclaré dans le schéma.
            </p>
          )}
          {fields.map((field) => (
            <FieldRow
              key={field.key}
              field={field}
              value={data[field.key]}
              onChange={(v) => setField(field.key, v)}
              onOpenModal={(e) => setModal({ key: field.key, anchorEl: e?.currentTarget ?? null })}
            />
          ))}
        </section>

        {slugSection}
        {errorBanner}
      </main>

      {/* Éditeur ancré au champ, pour les types en mode 'modal' (image, lien,
          fond…) : même surface que sur le canvas — aucune modale centrée. */}
      {popover}
    </div>
  )
}

/**
 * Le rendu qu'un modèle AFFICHE, quand il n'est pas celui qu'on écrit.
 *
 * Un champ `rich-text` est stocké `{value, format}` et servi avec, en plus, un
 * `processed` calculé par les filtres Drupal du format — c'est cette clé que le
 * gabarit affiche. L'éditeur, lui, ne produit que la source : sans ce miroir,
 * modifier un corps de texte le ferait DISPARAÎTRE à l'écran jusqu'au prochain
 * chargement. On y recopie donc la source, en APPROXIMATION assumée du rendu
 * serveur (les filtres du format ne tournent pas ici) ; le vrai rendu revient au
 * prochain GET, et `processed` est de toute façon restrippé au presave.
 */
const withLocalRender = (fields, key, value) => {
  const field = fields.find(f => f && f.key === key)
  const type = field?.type ?? field?.editorType
  if (type !== 'rich-text') return value
  if (value == null || typeof value !== 'object' || Array.isArray(value)) return value
  return { ...value, processed: value.value ?? '' }
}

/**
 * Le sélecteur de modèle — « dans quoi je regarde ce contenu ».
 *
 * Une liste native : les modèles d'un type se comptent sur les doigts d'une
 * main (une page par gabarit d'affichage), et le contrôle natif est le plus
 * accessible. Aucun modèle déclaré → rien du tout : l'option n'existe pas
 * encore pour ce type, et un sélecteur à une seule entrée n'apprendrait rien.
 */
const ModelPicker = ({ models, loading, value, onChange }) => {
  if (loading || models.length === 0) return null
  return (
    <div className="flex items-center gap-1.5">
      <LayoutTemplate size={14} className="text-gray-400" aria-hidden="true" />
      <label htmlFor="object-model-picker" className="sr-only">Modèle d'affichage</label>
      <select
        id="object-model-picker"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="px-2 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-600"
      >
        <option value="">Formulaire</option>
        {models.map(m => (
          <option key={m.slug} value={m.slug}>{m.title}</option>
        ))}
      </select>
    </div>
  )
}

/**
 * Rendu d'un champ unique du schéma. Pour les éditeurs en mode `inline`,
 * la cellule rend un input directement contre lequel l'utilisateur tape ;
 * pour ceux en mode `modal`, on rend la cellule preview (clic ouvre la
 * pop-in ancrée via `onOpenModal`).
 */
const FieldRow = ({ field, value, onChange, onOpenModal }) => {
  const typeKey = field.type || field.editorType
  const plug = getEditor(typeKey)
  if (!plug) {
    return (
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1.5">
          {field.label || field.key}
        </label>
        <p className="text-xs text-red-600">Type d'éditeur inconnu : « {typeKey} »</p>
      </div>
    )
  }
  const { Cell, mode } = plug

  // Un champ à surface `modal` (image, lien, icône, référence, liste…) n'a
  // AUCUN moyen de se vider : son éditeur ne sait que choisir une valeur. Un
  // champ `inline` s'efface en effaçant son texte — il n'a rien à gagner à un
  // bouton de plus. Un champ requis ne se vide pas : c'est le seul endroit où
  // `required:` veut dire quelque chose dans ce formulaire.
  const canClear = mode !== 'inline' && !field.required && hasFieldValue(value)

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <label className="block text-xs font-medium text-gray-500">
          {field.label || field.key}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {canClear && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_FIELD_VALUE)}
            className="text-xs text-gray-500 hover:text-red-600 transition-colors"
          >
            Vider<span className="sr-only"> le champ {field.label || field.key}</span>
          </button>
        )}
      </div>
      {(() => {
        const { key: _ignored, ...config } = field
        if (mode === 'inline' && Cell) {
          return (
            <Cell
              value={value}
              onChange={onChange}
              placeholder={field.placeholder ?? ''}
              {...config}
            />
          )
        }
        // Modal cell (preview button) — or fallback to a basic preview.
        if (Cell) {
          return <Cell value={value} onClick={onOpenModal} {...config} />
        }
        return (
          <button
            type="button"
            onClick={onOpenModal}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:border-teal-400 text-gray-600 text-left w-full"
          >
            {value ? '✓ défini' : 'Éditer…'}
          </button>
        )
      })()}
      {field.description && (
        <p className="text-xs text-gray-400 mt-1">{field.description}</p>
      )}
    </div>
  )
}
